import ModalComponent from "../../../modals/ModalFormComponent";
import { formatDateAndTime } from "../../../../helpers/appHelpers";
import {
	AuditLogDto,
	AuditLogJson,
	AuditLogSnapshot,
} from "../../../../models/audit-log.dto";
import { AuditLogOutcomeChip, getAuditLogUserName, jsonToText } from "./data";

type AuditLogDetailsModalProps = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	auditLog: AuditLogDto;
	companyName?: string;
};

type ChangeType = "CREATED" | "UPDATED" | "DELETED" | "UNCHANGED" | "UNKNOWN";

const CHANGE_TYPE_DISPLAY: Record<ChangeType, { label: string; colors: string }> = {
	CREATED: { label: "Created", colors: "bg-green-100 text-green-700" },
	UPDATED: { label: "Updated", colors: "bg-blue-100 text-blue-600" },
	DELETED: { label: "Deleted", colors: "bg-red-100 text-red-600" },
	UNCHANGED: { label: "Unchanged", colors: "bg-gray-100 text-gray-500" },
	UNKNOWN: { label: "Partially recorded", colors: "bg-amber-50 text-amber-700" },
};

const TRUNCATED_KEY = "_truncated";
const OMITTED_KEY = "_omitted";

const isPlainObject = (
	value: AuditLogJson | undefined,
): value is { [key: string]: AuditLogJson } =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isSameValue = (first: AuditLogJson | undefined, second: AuditLogJson | undefined) =>
	JSON.stringify(first) === JSON.stringify(second);

/** The reason a snapshot side was left out, when it was */
const getOmittedReason = (snapshot: AuditLogSnapshot | null) =>
	isPlainObject(snapshot as AuditLogJson) &&
	typeof (snapshot as { [key: string]: AuditLogJson })[OMITTED_KEY] === "string"
		? ((snapshot as { [key: string]: AuditLogJson })[OMITTED_KEY] as string)
		: null;

/** Whether an error is an envelope this view knows how to lay out */
const hasKnownErrorFields = (error: unknown): boolean =>
	isPlainObject(error as AuditLogJson) &&
	["error", "message", "method", "path"].some(
		(field) => (error as { [key: string]: unknown })[field] !== undefined,
	);

const DetailItem = ({
	label,
	children,
	fullWidth,
}: {
	label: string;
	children: React.ReactNode;
	fullWidth?: boolean;
}) => (
	<div className={fullWidth ? "col-span-2" : ""}>
		<p className="text-xs font-medium text-secondary-darkgray">{label}</p>
		<div className="text-sm mt-1 break-words">{children}</div>
	</div>
);

const JsonBlock = ({ value }: { value: AuditLogJson | undefined }) => (
	<pre className="text-xs bg-gray-50 rounded-lg px-3 py-2 overflow-auto max-h-[300px] whitespace-pre-wrap break-all">
		{typeof value === "string" ? value : JSON.stringify(value, null, 2)}
	</pre>
);

/** A value of a record field: text for primitives, formatted JSON otherwise */
const FieldValue = ({ value }: { value: AuditLogJson | undefined }) => {
	if (value === undefined) return <span className="text-gray-400">--</span>;
	if (value === null) return <span className="text-gray-400">null</span>;
	if (typeof value === "object") return <JsonBlock value={value} />;
	return <span className="break-all">{String(value)}</span>;
};

const SnapshotRecordDiff = ({
	recordKey,
	before,
	after,
	changeType,
}: {
	recordKey: string;
	before: AuditLogJson | undefined;
	after: AuditLogJson | undefined;
	changeType: ChangeType;
}) => {
	const display = CHANGE_TYPE_DISPLAY[changeType];
	// Records are normally objects; compare anything else as a single value
	const bothObjects =
		(before === undefined || isPlainObject(before)) &&
		(after === undefined || isPlainObject(after));
	const fields = bothObjects
		? Array.from(
			new Set([
				...Object.keys((before as object) || {}),
				...Object.keys((after as object) || {}),
			]),
		)
		: [];

	return (
		<div className="border rounded-lg overflow-hidden">
			<div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#F2F2F4]">
				<p className="text-sm font-semibold break-all">{recordKey}</p>
				<span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${display.colors}`}>
					{display.label}
				</span>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-xs">
					<thead>
						<tr className="text-left text-secondary-darkgray">
							<th className="px-4 py-2 font-medium w-[22%]">Field</th>
							<th className="px-4 py-2 font-medium w-[39%]">Before</th>
							<th className="px-4 py-2 font-medium w-[39%]">After</th>
						</tr>
					</thead>
					<tbody>
						{bothObjects ? (
							fields.map((field) => {
								const beforeValue = (before as any)?.[field];
								const afterValue = (after as any)?.[field];
								const changed = !isSameValue(beforeValue, afterValue);

								return (
									<tr
										key={field}
										className={`border-t align-top ${changed ? "bg-amber-50" : ""}`}
									>
										<td className={`px-4 py-2 break-all ${changed ? "font-semibold" : ""}`}>
											{field}
										</td>
										<td className="px-4 py-2"><FieldValue value={beforeValue} /></td>
										<td className="px-4 py-2"><FieldValue value={afterValue} /></td>
									</tr>
								);
							})
						) : (
							<tr
								className={`border-t align-top ${
									!isSameValue(before, after) ? "bg-amber-50" : ""
								}`}
							>
								<td className="px-4 py-2 text-gray-400">(value)</td>
								<td className="px-4 py-2"><FieldValue value={before} /></td>
								<td className="px-4 py-2"><FieldValue value={after} /></td>
							</tr>
						)}
						{bothObjects && fields.length === 0 && (
							<tr className="border-t">
								<td colSpan={3} className="px-4 py-2 text-gray-400">
									No fields were recorded
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
};

const DataChanges = ({
	dataBefore,
	dataAfter,
}: {
	dataBefore: AuditLogSnapshot | null;
	dataAfter: AuditLogSnapshot | null;
}) => {
	const beforeOmitted = getOmittedReason(dataBefore);
	const afterOmitted = getOmittedReason(dataAfter);
	const usableBefore = beforeOmitted ? null : dataBefore;
	const usableAfter = afterOmitted ? null : dataAfter;
	const truncated =
		usableBefore?.[TRUNCATED_KEY] === true || usableAfter?.[TRUNCATED_KEY] === true;

	const recordKeys = Array.from(
		new Set([
			...Object.keys(usableBefore || {}),
			...Object.keys(usableAfter || {}),
		]),
	).filter((key) => key !== TRUNCATED_KEY);

	const getChangeType = (key: string): ChangeType => {
		// One side was left out, so the record's change can't be classified
		if (beforeOmitted || afterOmitted) return "UNKNOWN";

		const inBefore = !!usableBefore && key in usableBefore;
		const inAfter = !!usableAfter && key in usableAfter;
		if (!inBefore && inAfter) return "CREATED";
		if (inBefore && !inAfter) return "DELETED";
		return isSameValue(usableBefore?.[key], usableAfter?.[key]) ? "UNCHANGED" : "UPDATED";
	};

	if (!dataBefore && !dataAfter) {
		return <p className="text-sm text-gray-400">No data changes were recorded</p>;
	}

	// Some producers store a snapshot that isn't a record map (e.g. a string
	// that wasn't valid JSON); show those sides as they are
	const notRecordMap = (snapshot: AuditLogSnapshot | null) =>
		snapshot !== null && snapshot !== undefined && !isPlainObject(snapshot as AuditLogJson);
	if (notRecordMap(dataBefore) || notRecordMap(dataAfter)) {
		return (
			<div className="grid grid-cols-2 gap-3">
				<div>
					<p className="text-xs font-medium text-secondary-darkgray mb-1">Before</p>
					<FieldValue value={(dataBefore ?? undefined) as AuditLogJson | undefined} />
				</div>
				<div>
					<p className="text-xs font-medium text-secondary-darkgray mb-1">After</p>
					<FieldValue value={(dataAfter ?? undefined) as AuditLogJson | undefined} />
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			{truncated && (
				<p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-4 py-2">
					This change set was too large to store in full, so some records are not shown.
				</p>
			)}
			{beforeOmitted && (
				<p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-4 py-2">
					The data before this action was not recorded: {beforeOmitted}
				</p>
			)}
			{afterOmitted && (
				<p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-4 py-2">
					The data after this action was not recorded: {afterOmitted}
				</p>
			)}
			{recordKeys.length === 0 && !beforeOmitted && !afterOmitted && (
				<p className="text-sm text-gray-400">No records were changed</p>
			)}
			{recordKeys.map((key) => (
				<SnapshotRecordDiff
					key={key}
					recordKey={key}
					before={usableBefore?.[key]}
					after={usableAfter?.[key]}
					changeType={getChangeType(key)}
				/>
			))}
		</div>
	);
};

const AuditLogDetailsModal = ({
	open,
	setOpen,
	auditLog,
	companyName,
}: AuditLogDetailsModalProps) => {
	const { user, error } = auditLog;

	return (
		<ModalComponent
			open={open}
			title="Audit Log Details"
			width={960}
			handleClose={() => setOpen(false)}
		>
			<div className="grid grid-cols-2 gap-x-4 gap-y-5">
				<DetailItem label="Action">{auditLog.action || "--"}</DetailItem>
				<DetailItem label="Organization">
					{auditLog.organization?.name || auditLog.organizationId || "--"}
				</DetailItem>
				<DetailItem label="Outcome">
					<div className="flex items-center gap-3">
						<AuditLogOutcomeChip outcome={auditLog.outcome} />
						{auditLog.statusCode !== null && auditLog.statusCode !== undefined && (
							<span className="text-secondary-darkgray">
								Status {auditLog.statusCode}
							</span>
						)}
					</div>
				</DetailItem>
				<DetailItem label="Service">{auditLog.service || "--"}</DetailItem>
				<DetailItem label="User">
					<p>{getAuditLogUserName(user)}</p>
					{user && (
						<p className="text-xs text-secondary-darkgray mt-0.5">
							{[
								user.username,
								user.category,
								user.employeeId ? `Employee #${user.employeeId}` : "",
								user.platformUser ? "Platform user" : "",
							].filter(Boolean).join(" · ")}
						</p>
					)}
				</DetailItem>
				<DetailItem label="Company">{companyName || "--"}</DetailItem>
				<DetailItem label="Timestamp">
					{auditLog.timestamp ? formatDateAndTime(auditLog.timestamp) : "--"}
				</DetailItem>
				<DetailItem label="Modified At">
					{auditLog.modifiedAt ? formatDateAndTime(auditLog.modifiedAt) : "--"}
				</DetailItem>
				<DetailItem label="Request ID" fullWidth>
					<span className="font-mono text-xs">{auditLog.requestId || "--"}</span>
				</DetailItem>
				{error && !hasKnownErrorFields(error) && (
					<DetailItem label="Error" fullWidth>
						<JsonBlock value={error as AuditLogJson} />
					</DetailItem>
				)}
				{error && hasKnownErrorFields(error) && (
					<DetailItem label="Error" fullWidth>
						<div className="text-secondary-red bg-red-50 rounded-lg px-4 py-3 flex flex-col gap-1">
							{error.error !== undefined && (
								<p className="font-semibold">{jsonToText(error.error)}</p>
							)}
							{error.message !== undefined && <p>{jsonToText(error.message)}</p>}
							{(error.method !== undefined || error.path !== undefined) && (
								<p className="font-mono text-xs">
									{[jsonToText(error.method), jsonToText(error.path)]
										.filter(Boolean)
										.join(" ")}
								</p>
							)}
						</div>
						{error.cause !== undefined && (
							<div className="mt-2">
								<p className="text-xs font-medium text-secondary-darkgray mb-1">Cause</p>
								<JsonBlock value={error.cause} />
							</div>
						)}
					</DetailItem>
				)}
				<DetailItem label="Input" fullWidth>
					{auditLog.input === null || auditLog.input === undefined
						? "--"
						: <JsonBlock value={auditLog.input} />
					}
				</DetailItem>
				<DetailItem label="Data Changes" fullWidth>
					<DataChanges
						dataBefore={auditLog.dataBefore}
						dataAfter={auditLog.dataAfter}
					/>
				</DetailItem>
			</div>
		</ModalComponent>
	);
};

export default AuditLogDetailsModal;
